import { collection, doc, setDoc, query, onSnapshot, Unsubscribe, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { DownloadLog } from '../types';
import { perfTracker } from './perfLogger';

const DOWNLOADS_COLLECTION = 'downloads';

const downloadSubscribers = new Set<(downloads: DownloadLog[]) => void>();
let activeDownloadsUnsub: Unsubscribe | null = null;

export async function fetchDownloadsFromFirestore(): Promise<DownloadLog[]> {
  const startTime = performance.now();
  try {
    const q = query(collection(db, DOWNLOADS_COLLECTION));
    const snapshot = await getDocs(q);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(DOWNLOADS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'server', snapshot.size);

    const fetched: DownloadLog[] = [];
    snapshot.forEach((docSnap) => {
      fetched.push({ id: docSnap.id, ...docSnap.data() } as DownloadLog);
    });
    return fetched;
  } catch (err) {
    console.error('Fetch downloads error:', err);
    return [];
  }
}

export function subscribeToDownloadsInFirestore(onDownloadsUpdate: (downloads: DownloadLog[]) => void): () => void {
  if (downloadSubscribers.has(onDownloadsUpdate)) {
    perfTracker.logDuplicatePrevented(DOWNLOADS_COLLECTION);
    return () => downloadSubscribers.delete(onDownloadsUpdate);
  }

  downloadSubscribers.add(onDownloadsUpdate);

  if (!activeDownloadsUnsub) {
    perfTracker.registerListenerStart(DOWNLOADS_COLLECTION);
    const q = query(collection(db, DOWNLOADS_COLLECTION));

    activeDownloadsUnsub = onSnapshot(
      q,
      (snapshot) => {
        const startTime = performance.now();
        const fetched: DownloadLog[] = [];
        snapshot.forEach((docSnap) => {
          fetched.push({ id: docSnap.id, ...docSnap.data() } as DownloadLog);
        });

        const duration = performance.now() - startTime;
        perfTracker.logFetch(DOWNLOADS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'snapshot', fetched.length);

        downloadSubscribers.forEach((cb) => {
          try { cb(fetched); } catch (e) {}
        });
      },
      (err) => {
        console.error('Downloads snapshot error:', err);
      }
    );
  } else {
    perfTracker.logDuplicatePrevented(DOWNLOADS_COLLECTION);
  }

  return () => {
    downloadSubscribers.delete(onDownloadsUpdate);
    if (downloadSubscribers.size === 0 && activeDownloadsUnsub) {
      activeDownloadsUnsub();
      activeDownloadsUnsub = null;
      perfTracker.registerListenerStop(DOWNLOADS_COLLECTION);
    }
  };
}

export async function logDownloadInFirestore(productId: string, userId?: string): Promise<void> {
  try {
    const id = `dl_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const log: DownloadLog = {
      id,
      productId,
      userId: userId || 'anonymous',
      timestamp: new Date().toISOString()
    };
    await setDoc(doc(db, DOWNLOADS_COLLECTION, id), log);
  } catch (err) {
    console.error('Error logging download in Firestore:', err);
  }
}
